/**
 * UI slice — global UI state that doesn't belong in any single feature.
 *
 * Covers the two main structural controls of the management dashboard:
 *   - sidebarCollapsed: whether the left nav sidebar is folded to icon-only mode
 *   - drawerOpen: whether a modal drawer/bottom-sheet is visible (mobile overlay)
 *
 * Both default to `false` (expanded sidebar, drawer closed).
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface UiState {
  /**
   * When `true` the sidebar is collapsed to icon-only mode.
   * When `false` (default) the full-width labelled sidebar is shown.
   */
  sidebarCollapsed: boolean;
  /**
   * Controls the global mobile drawer overlay.
   * `false` (default) = closed.
   */
  drawerOpen: boolean;
}

const initialState: UiState = {
  sidebarCollapsed: false,
  drawerOpen: false,
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    /** Toggle sidebar between collapsed and expanded. */
    toggleSidebar(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    /** Explicitly set sidebar collapsed state. */
    setSidebarCollapsed(state, action: PayloadAction<boolean>) {
      state.sidebarCollapsed = action.payload;
    },
    /** Open the mobile drawer. */
    openDrawer(state) {
      state.drawerOpen = true;
    },
    /** Close the mobile drawer. */
    closeDrawer(state) {
      state.drawerOpen = false;
    },
    /** Toggle the mobile drawer. */
    toggleDrawer(state) {
      state.drawerOpen = !state.drawerOpen;
    },
  },
});

export const {
  toggleSidebar,
  setSidebarCollapsed,
  openDrawer,
  closeDrawer,
  toggleDrawer,
} = uiSlice.actions;

export default uiSlice.reducer;
